import React from 'react';
import { SlideContainer } from './SlideContainer';
import slideValoresImg from '../assets/SlideValores.jpg';
import { ISlidePropostaData } from '../interface';

import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export interface SlideValoresProps {
  data?: ISlidePropostaData;
  scale?: number;
}

const formatarMoeda = (valor?: number | null) => {
  if (valor === undefined || valor === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
};

const formatarDataAssinatura = (data?: string | Date | null) => {
  try {
    const d = data ? new Date(data) : new Date();
    if (isNaN(d.getTime())) return format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    return format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  }
};

export const SlideValores: React.FC<SlideValoresProps> = ({ data, scale = 1 }) => {
  const tituloCabecalho = (data?.titulo_cabecalho || 'PROPOSTA COMERCIAL AO MUNICÍPIO DE').toUpperCase();
  const cidadeNome = (data?.cidade_nome || 'CORURIPE').toUpperCase();
  const cidadeUf = (data?.cidade_uf || '').toUpperCase();
  const planoNome = data?.plano_nome || 'Licença Intermediária';
  
  const qtdLicencas = data?.quantidade_licencas || 1;
  const valorUnitarioPlano = Number(data?.valor_unitario_plano || 0) > 0
    ? Number(data?.valor_unitario_plano)
    : (Number(data?.valor_total_plano || 0) > 0 ? Number(data?.valor_total_plano) / qtdLicencas : 0);
  const valorTotalPlano = Number(data?.valor_total_plano || 0) > 0
    ? Number(data?.valor_total_plano)
    : (qtdLicencas * valorUnitarioPlano);

  const temHoras = Boolean(data?.contratacao_horas && data?.quantidade_horas && data.quantidade_horas > 0);
  const qtdHoras = data?.quantidade_horas || 0;
  const valorUnitarioHoras = Number(data?.valor_unitario_horas || 0) > 0
    ? Number(data?.valor_unitario_horas)
    : (Number(data?.valor_total_horas || 0) > 0 && qtdHoras > 0 ? Number(data?.valor_total_horas) / qtdHoras : 0);
  const valorTotalHoras = Number(data?.valor_total_horas || 0) > 0
    ? Number(data?.valor_total_horas)
    : (qtdHoras * valorUnitarioHoras);

  const valorTotalGeral = Number(data?.valor_total_geral || 0) > 0
    ? Number(data?.valor_total_geral)
    : (valorTotalPlano + valorTotalHoras);

  const possuiTela = data?.possui_tela ?? true;
  const condicaoPagamento = data?.condicao_pagamento || 'parcela_unica';
  const formaPagamentoDescricao = data?.forma_pagamento_descricao;

  const dataAssinatura = formatarDataAssinatura(data?.gerado_em);

  const tipoTela = data?.tipo_tela;

  const descricaoTela = tipoTela === "totem"
    ? '1 (um) Totem Interativo de Led 32 polegadas e seu suporte'
    : tipoTela === "tela85"
    ? '1 (uma) Tela Interativa de Led 85 polegadas e seu suporte'
    : '1 (uma) Tela Interativa de Led 75 polegadas e seu suporte';

  return (
    <SlideContainer 
      showFooterLogo={false} 
      showWaves={false} 
      noPadding={true}
      backgroundImage={slideValoresImg}
      scale={scale}
      data={data}
    >
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        
        {/* Título Dinâmico */}
        <div
          style={{
            position: 'absolute',
            top: '6.5%',
            left: '32%',
            width: '65%',
            color: '#0077b6',
            fontWeight: 900,
            fontStyle: 'italic',
            fontSize: 38,
            lineHeight: 1.15,
            letterSpacing: 0.5,
          }}
        >
          {tituloCabecalho}<br />
          {cidadeNome}{cidadeUf ? ` - ${cidadeUf}` : ''}
        </div>

        {/* Container das Tabelas e Total */}
        <div
          style={{
            position: 'absolute',
            top: '16.5%',
            left: '4%',
            width: '92%',
          }}
        >
          {/* Tabela 1: Licenças */}
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 19,
              marginBottom: temHoras ? '4px' : '0',
            }}
          >
            <thead>
              <tr style={{ background: '#0077b6', color: '#fff' }}>
                <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '38%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                  Descrição
                </th>
                <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '12%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                  Und.
                </th>
                <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '18%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                  {/* {temHoras ? "Qtd. de Licenças/Horas" : "Qtd. de Licenças"} */} Qtd. de Licenças
                </th>
                <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '16%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                  Valor Unitário
                </th>
                <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '16%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: '#fff' }}>
                <td style={{ padding: '10px 12px', border: '2px solid #0077b6', fontWeight: 700, textAlign: 'center', color: '#000' }}>
                  {data?.descricao_plano || `${planoNome} do aplicativo Govfácil com acesso`}
                </td>
                <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                  Anual
                </td>
                <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                  {qtdLicencas}
                </td>
                <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                  {formatarMoeda(valorUnitarioPlano)}
                </td>
                <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                  {formatarMoeda(valorTotalPlano)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Tabela 2: Horas Técnicas (apenas se houver contratação de horas) */}
          {temHoras && (
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 19,
                position: 'relative',
                top: '8px'
              }}
            >
              <thead>
                <tr style={{ background: '#0077b6', color: '#fff' }}>
                  <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '38%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                    Descrição
                  </th>
                  <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '12%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                    Und.
                  </th>
                  <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '18%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                    Quantidade
                  </th>
                  <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '16%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                    Valor Unitário
                  </th>
                  <th style={{ padding: '10px 8px', border: '2px solid #0077b6', width: '16%', textAlign: 'center', fontSize: 20, fontWeight: 800 }}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: '#fff' }}>
                  <td style={{ padding: '10px 12px', border: '2px solid #0077b6', fontWeight: 700, textAlign: 'center', color: '#000' }}>
                    Serviço de implantação, customização personalizada e parametrização (sob demanda)
                  </td>
                  <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                    Horas
                  </td>
                  <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                    {new Intl.NumberFormat('pt-BR').format(qtdHoras)}
                  </td>
                  <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                    {formatarMoeda(valorUnitarioHoras)}
                  </td>
                  <td style={{ padding: '10px 8px', border: '2px solid #0077b6', textAlign: 'center', fontWeight: 700, color: '#000' }}>
                    {formatarMoeda(valorTotalHoras)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Linha de Total Geral */}
          <div
            style={{
              textAlign: 'right',
              marginTop: 8,
              fontSize: temHoras ? 28 : 34,
              fontWeight: 900,
              color: '#000',
              letterSpacing: '-0.5px',
            }}
          >
            {temHoras ? `Valor Total ${formatarMoeda(valorTotalGeral)}` : `Total ${formatarMoeda(valorTotalGeral)}`}
          </div>
          {/* {temHoras && (
            <div
              style={{
                textAlign: 'right',
                fontSize: 15,
                fontWeight: 700,
                color: '#444',
                marginTop: 2,
              }}
            >
              (Licenciamento: {formatarMoeda(valorTotalPlano)} | Horas sob demanda: até {formatarMoeda(valorTotalHoras)})
            </div>
          )} */}
        </div>

        {/* Detalhamento das Condições */}
        <div
          style={{
            position: 'absolute',
            top: temHoras ? '47%' : '44%',
            left: '4%',
            width: '58%',
            fontSize: temHoras ? 17.5 : 19,
            lineHeight: 1.38,
            color: '#111827',
          }}
        >
          <p style={{ fontWeight: 800, textAlign: 'justify', textDecoration: 'underline', marginBottom: 6, fontSize: 24, color: '#000' }}>
            Detalhamento:
          </p>
          {(() => {
            const detalhamentoRaw = data?.detalhamento || data?.detalhamento_padrao;

            if (detalhamentoRaw && detalhamentoRaw.trim()) {
              // Substitui variáveis
              const parsed = detalhamentoRaw
                .replace(/\{\{acessos\}\}/gi, qtdLicencas === 1 ? '1 acesso' : `${qtdLicencas} acessos`)
                .replace(/\{\{qtd_licencas\}\}/gi, String(qtdLicencas))
                .replace(/\{\{equipamento\}\}/gi, descricaoTela)
                .replace(/\{\{qtd_horas\}\}/gi, new Intl.NumberFormat('pt-BR').format(qtdHoras))
                .replace(/\{\{valor_hora\}\}/gi, formatarMoeda(valorUnitarioHoras))
                .replace(/\{\{forma_pagamento\}\}/gi, formaPagamentoDescricao || '')
                .replace(/\{\{detalhamento_horas\}\}/gi, data?.detalhamento_horas || '')
                .replace(/\{\{validade\}\}/gi, '60 DIAS');

              const rawLines = parsed.split('\n');

              const renderInline = (content: string) => {
                const parts = content.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~|```[^`]+```)/g);
                return parts.map((part, pIdx) => {
                  if (part.startsWith('*') && part.endsWith('*') && part.length >= 2) {
                    return <strong key={pIdx}>{part.slice(1, -1)}</strong>;
                  }
                  if (part.startsWith('_') && part.endsWith('_') && part.length >= 2) {
                    return <em key={pIdx}>{part.slice(1, -1)}</em>;
                  }
                  if (part.startsWith('~') && part.endsWith('~') && part.length >= 2) {
                    return <del key={pIdx}>{part.slice(1, -1)}</del>;
                  }
                  if (part.startsWith('```') && part.endsWith('```') && part.length >= 6) {
                    return <code key={pIdx} style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>{part.slice(3, -3)}</code>;
                  }
                  return part;
                });
              };

              const bulletItems: React.ReactNode[] = [];
              const footerItems: React.ReactNode[] = [];

              rawLines.forEach((line, idx) => {
                const trimmed = line.trim();
                if (!trimmed) return;

                const isValidade = trimmed.toUpperCase().includes('VALIDADE PROPOSTA');
                if (isValidade) {
                  footerItems.push(
                    <p key={`val-${idx}`} style={{ fontWeight: 900, marginTop: 14, fontSize: 25, color: '#000', textAlign: 'justify' }}>
                      {renderInline(trimmed.replace(/^\*\s*|\s*\*$/g, ''))}
                    </p>
                  );
                  return;
                }

                const isBullet = trimmed.startsWith('•') || trimmed.startsWith('-');
                const lineContent = isBullet ? trimmed.replace(/^[•\-]\s*/, '') : trimmed;

                bulletItems.push(
                  <li key={`item-${idx}`} style={{ marginBottom: 4, textAlign: 'justify' }}>
                    {renderInline(lineContent)}
                  </li>
                );
              });

              if (temHoras && data?.detalhamento_horas && !detalhamentoRaw.toLowerCase().includes('{{detalhamento_horas}}')) {
                bulletItems.push(
                  <li key="item-detalhamento-horas" style={{ marginBottom: 4, textAlign: 'justify' }}>
                    {renderInline(data.detalhamento_horas)}
                  </li>
                );
              }

              return (
                <>
                  {bulletItems.length > 0 && (
                    <ul style={{ paddingLeft: '1.2rem', margin: 0, listStyleType: 'disc', fontSize: temHoras && data?.detalhamento_horas ? '1.35rem' : '1.5rem' }}>
                      {bulletItems}
                    </ul>
                  )}
                  {footerItems}
                </>
              );
            }

            return (
              <>
                <ul style={{ paddingLeft: '1.2rem', margin: 0, listStyleType: 'disc', fontSize: temHoras && data?.detalhamento_horas ? '1.35rem' : '1.5rem' }}>
                  <li style={{ marginBottom: 4, textAlign: 'justify' }}>
                    Trata-se de licença para uso da ferramenta de gestão Govfácil App, onde cada
                    licença possui {qtdLicencas === 1 ? '1 acesso' : `${qtdLicencas} acessos`} ao aplicativo. As licenças do aplicativo se classificam como
                    contínuas.
                  </li>
                  {possuiTela && (
                    <li style={{ marginBottom: 4, textAlign: 'justify' }}>
                      Concessão de uso de {descricaoTela} em comodato{' '}
                      <strong>por cada licença</strong>, pelo período vigente do contrato.
                    </li>
                  )}
                  {temHoras && (
                    <li style={{ marginBottom: 4, textAlign: 'justify' }}>
                      A contratação também inclui um total de {new Intl.NumberFormat('pt-BR').format(qtdHoras)} horas técnicas de serviços, com custo
                      de {formatarMoeda(valorUnitarioHoras)}/hora, que são necessárias para customização personalizada,
                      implantação, treinamento, capacitação, integrações de dados e parametrizações para adaptar as
                      configurações do sistema adequando ao Contratante.
                    </li>
                  )}
                  {temHoras && data?.detalhamento_horas && (
                    <li style={{ marginBottom: 4, textAlign: 'justify' }}>
                      {data.detalhamento_horas}
                    </li>
                  )}
                  <li style={{ marginBottom: 4, textAlign: 'justify' }}>
                    Prazo das licenças: <strong>12 MESES</strong>, sendo a renovação automática e faturada conforme proposta a
                    ser enviada 30 dias antes do vencimento.
                  </li>
                  <li style={{ marginBottom: 4, textAlign: 'justify' }}>
                    <>
                      Forma de Pagamento: {formaPagamentoDescricao}
                    </>
                  </li>
                </ul>
                <p style={{ fontWeight: 900, marginTop: 14, fontSize: 25, color: '#000', textAlign: 'justify' }}>
                  VALIDADE PROPOSTA: 60 DIAS.
                </p>
              </>
            );
          })()}
        </div>

        {/* Bloco de Dados Institucionais abaixo da Linha de Assinatura */}
        <div
          style={{
            position: 'relative',
            top: '74%',
            left: '65%',
            width: '30%',
            textAlign: 'left',
            color: '#111827',
            lineHeight: 1.35,
          }}
        >
          <div style={{ fontWeight: 900, fontSize: 21, color: '#000000', letterSpacing: 0.2 }}>
            GOVFÁCIL GESTÃO &amp; TECNOLOGIA LTDA.
          </div>
          <div style={{ fontWeight: 800, fontSize: 20, color: '#000000', marginTop: 3 }}>
            Eliane M. Barzon da Costa
          </div>
          <div style={{ fontSize: 20, color: '#1f2937', marginTop: 2, fontWeight: 500 }}>
            Sócia Proprietária
          </div>
          <div style={{ fontSize: 19, color: '#1f2937', marginTop: 2, fontWeight: 500 }}>
            Londrina-PR, {dataAssinatura}
          </div>
        </div>

      </div>
    </SlideContainer>
  );
};